// src/components/WindPanel.jsx
// =====================  WHERE THIS GOES  =====================
// New file. Twin of SunPathPanel.jsx. Rendered by ClimateAnalysisPanel
// when the user opens "Wind Path". Talks to the 3D scene only through
// windStore. Works before any massing/fetch exists: the manual dir/speed
// override drives the arrow immediately; NASA POWER fills the rose when
// asked. `dir` is always the FROM bearing (met convention).
//
// API_BASE mirrors Site3DView / CostAnalysisPanel — centralise later.
// ============================================================
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { MONTHS } from '../utils/solar';
import { useWind, setResults } from '../utils/windStore';
import { dirLabel16, windTravelBearing, pressureCss, roseFromMonths, annualEnergy, monthlyEnergyKWh, ROLE_LABEL } from '../utils/wind';
import { API_BASE } from '../utils/apiBase';

const FACES = [{ key: 'N', name: 'North' }, { key: 'E', name: 'East' }, { key: 'S', name: 'South' }, { key: 'W', name: 'West' }];

export default function WindPanel({ project }) {
  const { params, results, status, rose, setParams, setRose, runAnalysis, activate, deactivate } = useWind();
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);
  const [energy, setEnergy] = useState({ rotorD: 3, cp: 0.35, eta: 0.85, hubH: 6, alpha: 0.27, roofSpeedup: false });

  useEffect(() => { activate(); return () => deactivate(); }, []); // eslint-disable-line

  // seed lat/lng from project origin + load cached rose if present
  useEffect(() => {
    const lat = project?.centerLat, lng = project?.centerLng;
    if (lat != null && lng != null) setParams({ lat: +lat, lng: +lng });
    if (project?.cachedWind?.perMonth) { setRose(project.cachedWind); seedMonth(project.cachedWind, params.month); }
    // eslint-disable-next-line
  }, [project?.id]);

  // when month changes and we're on a fetched rose, follow that month's prevailing
  useEffect(() => {
    if (!params.manual && rose?.perMonth) seedMonth(rose, params.month);
    // eslint-disable-next-line
  }, [params.month]);

  const seedMonth = (r, month) => {
    const pm = r.perMonth?.find((m) => m.month === month);
    if (pm && pm.prevailingDir != null && pm.meanSpeed != null) {
      setParams({ dir: Math.round(pm.prevailingDir), speed: +(+pm.meanSpeed).toFixed(1), manual: false });
    }
  };

  const persistWind = async (payload) => {
    if (project?.id == null) return;
    try {
      const body = { cachedWind: { ...payload, fetchedAt: new Date().toISOString() } };
      const res = await fetch(`${API_BASE}/api/projects/${project.id}`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body),
      });
      if (res.ok) Object.assign(project, body);
    } catch (e) { console.warn('wind cache persist error', e); }
  };

  const fetchRose = async () => {
    setLoading(true); setErr(null);
    try {
      const r = await fetch(`${API_BASE}/api/wind?lat=${params.lat}&lng=${params.lng}`);
      const d = await r.json();
      if (!d.ok) throw new Error(d.error || 'wind fetch failed');
      setRose(d); seedMonth(d, params.month); persistWind(d);
    } catch (e) { setErr(e.message || 'Could not reach NASA POWER'); }
    finally { setLoading(false); }
  };

  const roseBins = useMemo(() => (rose?.perMonth ? roseFromMonths(rose.perMonth) : null), [rose]);
  const energyOpts = { rotorD: +energy.rotorD, cp: +energy.cp, eta: +energy.eta, hubH: +energy.hubH, alpha: +energy.alpha, roofSpeedup: energy.roofSpeedup ? 1.25 : 1 };
  const monthKWh = params.speed != null ? monthlyEnergyKWh(params.speed, energyOpts) : null;
  const annual = rose?.perMonth ? annualEnergy(rose.perMonth, energyOpts) : null;

  // ---- styles (identical tokens to SunPathPanel) ----
  const card = { background: 'var(--surface-2,#1a2234)', border: '0.5px solid var(--border,#3a4456)', borderRadius: 12, padding: 16, marginBottom: 12 };
  const lblRow = { display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', fontSize: 12, marginBottom: 6, color: 'var(--text-secondary,#c3c9d4)' };
  const val = { fontFamily: 'ui-monospace,Menlo,monospace', color: 'var(--text-primary,#e5e7eb)', fontSize: 13 };
  // boxSizing is the fix: width:100% plus padding and a border, with the default
  // content-box, made each of these WIDER than the third of a row it sits in —
  // so three side by side overflowed and clipped their own values. minWidth:0
  // lets a flex child actually shrink; without it the text still wins.
  //
  // --field rather than --surface-1: the card behind these is --surface-1 too,
  // so the box was the colour of its own background and only a half-pixel
  // border said it was a field at all.
  const numIn = {
    width: '100%', minWidth: 0, boxSizing: 'border-box',
    padding: '6px 8px', fontSize: 12.5,
    fontFamily: 'ui-monospace,Menlo,monospace',
    border: '1px solid var(--field-border, #818d9c)', borderRadius: 8,
    background: 'var(--field, #fff)', color: 'var(--field-ink, #111827)',
  };
  // INVALIDATE on any result-changing param (see SunPathPanel): showing a
  // previous run's numbers under new inputs is stale data with a fresh label.
  const firstParams = useRef(true);
  useEffect(() => {
    if (firstParams.current) { firstParams.current = false; return; }
    setResults(null);
    // eslint-disable-next-line
  }, [params.month, params.dir, params.speed]);

  // Blue at rest, green while active — matches the Design Analysis button.
  const btn = (a, disabled) => ({
    flex: 1, padding: '8px 10px', fontSize: 12, fontWeight: 600, cursor: disabled ? 'not-allowed' : 'pointer', borderRadius: 8,
    border: `0.5px solid ${a ? 'var(--ok,#3ecf8e)' : 'var(--border-accent,#378add)'}`,
    background: a ? 'var(--ok,#3ecf8e)' : 'var(--border-accent,#378add)',
    color: a ? '#01212a' : '#fff', opacity: disabled ? 0.45 : 1,
  });
  const Toggle = ({ k, label, disabled }) => (
    <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, cursor: disabled ? 'not-allowed' : 'pointer', opacity: disabled ? 0.45 : 1, color: params[k] ? 'var(--text-primary,#e5e7eb)' : 'var(--text-secondary,#8b92a0)' }}>
      <input type="checkbox" checked={!!params[k]} disabled={disabled} onChange={(e) => setParams({ [k]: e.target.checked })} style={{ accentColor: 'var(--border-accent,#378add)' }} />
      {label}
    </label>
  );

  const max = results ? Math.max(0.001, ...Object.values(results.totals)) : 1;
  const ordered = results ? [...FACES].sort((a, b) => results.totals[b.key] - results.totals[a.key]) : FACES;
  const travelDeg = windTravelBearing(params.dir);

  return (
    <div style={{ fontFamily: 'inherit' }}>
      {/* preconditions */}
      {!status.hasMassing && (
        <div style={{ ...card, border: '1px solid var(--border-accent)', color: 'var(--text-secondary,#c3c9d4)', fontSize: 12 }}>
          Build the massing first (left panel → Build) to score windward/leeward facades. The arrow, rose & sliders still work below.
        </div>
      )}
      {status.hasMassing && !status.hasContext && (
        <div style={{ ...card, border: '1px solid #8a6d1f', background: 'rgba(138,109,31,0.12)', color: '#e6c34d', fontSize: 12 }}>
          Enable <b>Site Context</b> in the 3D view to include shelter & funnelling from surrounding buildings — otherwise every windward facade reads fully exposed.
        </div>
      )}

      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, color: 'var(--text-muted,#8b92a0)', margin: '0 2px 12px' }}>
        <span>{MONTHS[params.month]} · from {Math.round(params.dir)}° {dirLabel16(params.dir)} · {(+params.speed).toFixed(1)} m/s</span>
        <span>{(+params.lat).toFixed(2)}°, {(+params.lng).toFixed(2)}°</span>
      </div>

      {/* wind source */}
      <div style={card}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
          <span style={{ fontSize: 13, fontWeight: 500 }}>Wind source</span>
          <span style={{ fontSize: 11, color: params.manual ? '#e6c34d' : 'var(--text-accent,#7fb4f0)' }}>{params.manual ? 'Manual override' : (rose?.source || 'NASA POWER')}</span>
        </div>
        <button style={btn(false, loading)} onClick={fetchRose} disabled={loading}>
          {loading ? 'Fetching…' : (rose ? '↻ Refresh prevailing wind' : 'Fetch prevailing wind (NASA POWER)')}
        </button>
        {err && <div style={{ fontSize: 11, color: '#e06c5b', marginTop: 8 }}>{err} — manual override still works.</div>}
        <div style={{ fontSize: 11, color: 'var(--text-muted,#8b92a0)', marginTop: 8, lineHeight: 1.5 }}>
          Monthly climatology gives a representative prevailing direction + mean speed — not a true frequency rose.
        </div>
      </div>

      {/* controls */}
      <div style={card}>
        <div style={lblRow}><span>Month</span><span style={val}>{MONTHS[params.month]}</span></div>
        <input type="range" min={0} max={11} step={1} value={params.month} onChange={(e) => setParams({ month: +e.target.value })} style={{ width: '100%', accentColor: '#378add' }} />

        <div style={{ ...lblRow, marginTop: 14 }}><span>Wind from</span><span style={val}>{Math.round(params.dir)}° {dirLabel16(params.dir)} → {Math.round(travelDeg)}° {dirLabel16(travelDeg)}</span></div>
        <input type="range" min={0} max={359} step={1} value={params.dir} onChange={(e) => setParams({ dir: +e.target.value, manual: true })} style={{ width: '100%', accentColor: '#378add' }} />

        <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 10, color: 'var(--text-muted,#8b92a0)', textTransform: 'uppercase', letterSpacing: '.4px', marginBottom: 4 }}>From °</div>
            <input type="number" step={1} value={Math.round(params.dir)} style={numIn} onChange={(e) => setParams({ dir: ((+e.target.value % 360) + 360) % 360, manual: true })} />
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 10, color: 'var(--text-muted,#8b92a0)', textTransform: 'uppercase', letterSpacing: '.4px', marginBottom: 4 }}>Speed m/s</div>
            <input type="number" step={0.1} value={params.speed} style={numIn} onChange={(e) => setParams({ speed: Math.max(0, parseFloat(e.target.value) || 0), manual: true })} />
          </div>
        </div>

        <div style={{ display: 'flex', gap: 8, marginTop: 14 }}>
          <button style={btn(params.mode === 'static')} onClick={() => setParams({ mode: 'static' })}>Static</button>
          <button style={btn(params.mode === 'dynamic')} onClick={() => setParams({ mode: 'dynamic' })}>Dynamic</button>
          <button style={btn(false, !status.hasMassing)} onClick={() => status.hasMassing && runAnalysis()} disabled={!status.hasMassing}>Analyze</button>
        </div>
      </div>

      {/* toggles */}
      <div style={card}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
          <Toggle k="showArrow" label="Prevailing arrow" />
          <Toggle k="showTint" label="Facade pressure" />
          <Toggle k="showRays" label="Shelter rays" />
          <Toggle k="showParticles" label="Streamlines" disabled={params.mode !== 'dynamic'} />
          <Toggle k="showTurbine" label="Roof turbine" />
        </div>
      </div>

      {/* rose */}
      {roseBins && (
        <div style={card}>
          <div style={{ fontSize: 13, fontWeight: 500, marginBottom: 2 }}>Prevailing-direction rose</div>
          <div style={{ fontSize: 11, color: 'var(--text-muted,#8b92a0)', marginBottom: 8 }}>months binned by prevailing direction · not a frequency rose</div>
          <Rose bins={roseBins} activeDeg={params.dir} />
        </div>
      )}

      {/* windward / leeward table */}
      <div style={card}>
        <div style={{ fontSize: 13, fontWeight: 500, marginBottom: 4 }}>Windward exposure by facade</div>
        <div style={{ fontSize: 11, color: 'var(--text-muted,#8b92a0)', marginBottom: 10 }}>exposure × unsheltered · indicative, not CFD</div>
        {!results && <div style={{ fontSize: 12, color: 'var(--text-secondary,#c3c9d4)' }}>Press <b>Analyze</b> to score facades against the {dirLabel16(params.dir)} wind.</div>}
        {results && ordered.map((F) => {
          const v = results.totals[F.key], frac = v / max;
          return (
            <div key={F.key} style={{ display: 'flex', alignItems: 'center', gap: 8, margin: '6px 0', fontSize: 12 }}>
              <span style={{ width: 44, color: 'var(--text-secondary,#c3c9d4)' }}>{F.name}</span>
              <span style={{ flex: 1, height: 9, background: 'var(--surface-1,#141a28)', borderRadius: 5, overflow: 'hidden' }}>
                <span style={{ display: 'block', height: '100%', width: `${(frac * 100).toFixed(0)}%`, background: pressureCss(frac), borderRadius: 5 }} />
              </span>
              <span style={{ width: 84, textAlign: 'right', fontSize: 11, color: 'var(--text-muted,#8b92a0)' }}>{(ROLE_LABEL[results.roles[F.key]] || 'Side')}</span>
            </div>
          );
        })}
        {results && (
          <div style={{ marginTop: 10, fontSize: 12, lineHeight: 1.5, color: 'var(--text-secondary,#c3c9d4)', background: 'var(--surface-1,#141a28)', border: '0.5px solid var(--border,#3a4456)', borderRadius: 8, padding: '9px 10px' }}>
            With wind from <b style={{ color: '#7fb4f0' }}>{dirLabel16(results.dir)}</b> ({(+results.speed).toFixed(1)} m/s), the <b style={{ color: '#ef6c3a' }}>{FACES.find((f) => f.key === results.hi)?.name}</b> facade is most exposed; the <b style={{ color: '#2f6fed' }}>{FACES.find((f) => f.key === results.lo)?.name}</b> facade is most sheltered.
          </div>
        )}
      </div>

      {/* rooftop energy */}
      <div style={card}>
        <div style={{ fontSize: 13, fontWeight: 500, marginBottom: 4 }}>Rooftop wind energy</div>
        <div style={{ fontSize: 11, color: 'var(--text-muted,#8b92a0)', marginBottom: 10 }}>feasibility screening · Rayleigh assumed · not a bankable yield</div>
        <div style={{ display: 'flex', gap: 8 }}>
          {[['rotorD', 'Rotor Ø m'], ['hubH', 'Hub h m'], ['cp', 'Cp'], ['eta', 'η']].map(([k, cap]) => (
            <div key={k} style={{ flex: 1 }}>
              <div style={{ fontSize: 10, color: 'var(--text-muted,#8b92a0)', textTransform: 'uppercase', letterSpacing: '.4px', marginBottom: 4 }}>{cap}</div>
              <input type="number" step={k === 'cp' || k === 'eta' ? 0.05 : 0.5} value={energy[k]} style={numIn} onChange={(e) => setEnergy((s) => ({ ...s, [k]: e.target.value }))} />
            </div>
          ))}
        </div>
        <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, cursor: 'pointer', marginTop: 10, color: 'var(--text-secondary,#c3c9d4)' }}>
          <input type="checkbox" checked={energy.roofSpeedup} onChange={(e) => setEnergy((s) => ({ ...s, roofSpeedup: e.target.checked }))} style={{ accentColor: 'var(--border-accent,#378add)' }} />
          Roof speed-up ×1.25 (optimistic — off by default)
        </label>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginTop: 12 }}>
          <div>
            <div style={{ fontSize: 10, color: 'var(--text-muted,#8b92a0)', textTransform: 'uppercase', letterSpacing: '.4px' }}>{MONTHS[params.month]} est.</div>
            <div style={{ ...val, fontSize: 16, marginTop: 2 }}>{monthKWh != null ? `${Math.round(monthKWh)} kWh` : '—'}</div>
          </div>
          <div>
            <div style={{ fontSize: 10, color: 'var(--text-muted,#8b92a0)', textTransform: 'uppercase', letterSpacing: '.4px' }}>Annual est.</div>
            <div style={{ ...val, fontSize: 16, marginTop: 2 }}>{annual ? `${Math.round(annual.annualKWh)} kWh` : 'fetch rose'}</div>
          </div>
        </div>
        <div style={{ fontSize: 11, color: 'var(--text-muted,#8b92a0)', marginTop: 8, lineHeight: 1.5 }}>
          Cp capped at Betz (0.593). Urban rooftop wind under-performs in practice — treat this as triage for ranking sites, not a yield promise.
        </div>
      </div>
    </div>
  );
}

// compact 16-sector rose (N up, clockwise). radial length ∝ months in sector.
function Rose({ bins, activeDeg }) {
  const S = 150, cx = S / 2, cy = S / 2, rMax = S / 2 - 16;
  const activeSector = Math.round((((activeDeg % 360) + 360) % 360) / 22.5) % 16;
  const pt = (deg, r) => [cx + r * Math.sin(deg * Math.PI / 180), cy - r * Math.cos(deg * Math.PI / 180)];
  return (
    <svg viewBox={`0 0 ${S} ${S}`} width="100%" height={S} style={{ maxWidth: 220, display: 'block', margin: '0 auto' }}>
      <circle cx={cx} cy={cy} r={rMax} fill="none" stroke="rgba(255,255,255,0.12)" />
      <circle cx={cx} cy={cy} r={rMax * 0.5} fill="none" stroke="rgba(255,255,255,0.08)" />
      {bins.map((b, i) => {
        if (b.count === 0) return null;
        const r = 6 + rMax * 0.94 * b.frac, c = i * 22.5;
        const [x1, y1] = pt(c - 10, r), [x2, y2] = pt(c + 10, r), [x0, y0] = [cx, cy];
        return <path key={i} d={`M${x0},${y0} L${x1},${y1} L${x2},${y2} Z`} fill={pressureCss(b.frac)} opacity={i === activeSector ? 1 : 0.7} stroke={i === activeSector ? '#e5e9f0' : 'none'} strokeWidth={i === activeSector ? 1 : 0} />;
      })}
      {[['N', 0], ['E', 90], ['S', 180], ['W', 270]].map(([l, d]) => {
        const [x, y] = pt(d, rMax + 9);
        return <text key={l} x={x} y={y + 3} textAnchor="middle" fontSize="10" fill="#8b92a0">{l}</text>;
      })}
    </svg>
  );
}
